import {
  Modal,
  Textarea,
  Group,
  Button,
  Input,
  ColorSwatch,
  Checkbox,
  Text,
  Box,
} from "@mantine/core";
import { DatePickerInput, DatesProvider } from "@mantine/dates";
import { useEffect, useState } from "react";
import { IconCalendar } from "../../../shared/svg/IconCalendar";
import { useTranslation } from "react-i18next";
import useProfileStore from "../../../store/useProfileStore";
import { CheckIconTask } from "../../../shared/svg/CheckIcon";
import useDevelopmentStore from "../../../store/useDevelopmentStore";
import dayjs from "dayjs";
import { useMediaQuery } from "@mantine/hooks";

const colorMap = {
  purple: "rgba(225, 215, 235)",
  red: "rgba(255, 215, 215)",
  yellow: "rgba(255, 248, 215)",
  green: "rgba(215, 255, 229)",
  blue: "rgba(215, 229, 255)",
  pink: "rgba(255, 215, 235)",
  orange: "rgba(255, 230, 215)",
  grey: "rgba(235, 235, 235)",
};

type TaskFormValues = {
  title: string;
  description: string;
  completed: boolean;
  day_from: string;
  day_to: string;
  display_color: string;
  reminder_me: boolean;
};

type Props = {
  opened: boolean;
  onClose: () => void;
  goalId: number | null;
  selectedDate?: dayjs.Dayjs | null;
};

export function CalendarAddTimestamp({
  opened,
  onClose,
  goalId,
  selectedDate,
}: Props) {
  const { t } = useTranslation();
  const { profileLanguage } = useProfileStore();
  const { addTask, currentPlanId, fetchLearningPlanGoals } =
    useDevelopmentStore();
  const [formErrors, setFormErrors] = useState<{
    title?: string;
    dateRange?: string;
  }>({});
  const matches = useMediaQuery("(max-width: 650px)");

  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    completed: boolean;
    reminder_me: boolean;
    dateRange: [Date | null, Date | null];
    display_color: string;
  }>({
    title: "",
    description: "",
    completed: false,
    reminder_me: true,
    dateRange: [null, null],
    display_color: "purple",
  });
  useEffect(() => {
    if (selectedDate) {
      setFormData((prev) => ({
        ...prev,
        dateRange: [selectedDate.toDate(), selectedDate.toDate()],
      }));
    }
  }, [selectedDate]);

  const handleSubmit = async () => {
    setFormErrors({});

    const [start, end] = formData.dateRange;
    const errors: { title?: string; dateRange?: string } = {};

    if (!formData.title.trim()) {
      errors.title = t("field_required");
    }

    if (!start || !end) {
      errors.dateRange = t("field_required");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const taskData: TaskFormValues = {
      title: formData.title,
      description: formData.description,
      completed: formData.completed,
      reminder_me: formData.reminder_me,
      day_from: dayjs(start).format("YYYY-MM-DD"),
      day_to: dayjs(end).format("YYYY-MM-DD"),
      display_color: formData.display_color,
    };

    try {
      if (currentPlanId && goalId) {
        await addTask({
          planId: currentPlanId!,
          goalId,
          credentials: taskData,
        });

        fetchLearningPlanGoals(currentPlanId!);

        onClose();
        setFormData({
          title: "",
          description: "",
          completed: false,
          reminder_me: true,
          dateRange: [null, null],
          display_color: "purple",
        });
      }
    } catch (err: any) {
      setFormErrors(
        Object.keys(err.response.data.errors).reduce((acc, key) => {
          acc[key] = err.response.data.errors[key][0];
          return acc;
        }, {} as { [key: string]: string })
      );
    }
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      description: "",
      completed: false,
      reminder_me: true,
      dateRange: [null, null],
      display_color: "purple",
    });
    onClose();
    setFormErrors({});
  };

  return (
    <Modal
    zIndex={301}

      opened={opened}
      onClose={handleCancel}
      title={<Text fw={600}>{t("form.addTask")}</Text>}
      centered
    >
      <Input.Wrapper
        label={t("form.title")}
        withAsterisk
        mt="sm"
        error={formErrors.title}
      >
        <Input
          data-autofocus
          placeholder={t("form.title")}
          value={formData.title}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setFormData((prev) => ({ ...prev, title: value }));
            if (formErrors.title) {
              setFormErrors((prev) => {
                const { title, ...rest } = prev;
                return rest;
              });
            }
          }}
          required
        />
      </Input.Wrapper>

      <Textarea
        label={t("form.description")}
        value={formData.description}
        autosize
        minRows={1}
        onChange={(e) => {
          const value = e.currentTarget.value;
          setFormData((prev) => ({
            ...prev,
            description: value,
          }));
        }}
        mt="md"
      />

      <Checkbox
        label={t("form.completed")}
        checked={formData.completed}
        onChange={(event) => {
          const value = event.currentTarget.checked;
          setFormData((prev) => ({
            ...prev,
            completed: value,
          }));
        }}
        mt="md"
        size="md"
      />

      <DatesProvider settings={{ locale: Object.keys(profileLanguage!)[0] }}>
        <DatePickerInput
          type="range"
          popoverProps={{
            zIndex: 302,
          }}
          allowSingleDateInRange
          label={`${t("form.startDate")} / ${t("form.endDate")}`}
          value={formData.dateRange}
          onChange={(range) => {
            setFormData((prev) => ({ ...prev, dateRange: range }));

            if (formErrors.dateRange) {
              setFormErrors((prev) => {
                const { dateRange, ...rest } = prev;
                return rest;
              });
            }
          }}
          placeholder={t("pick_daterange")}
          radius="md"
          size="md"
          variant="default"
          clearable
          leftSection={<IconCalendar />}
          mt="md"
          error={formErrors.dateRange}
          renderDay={(date) => {
            const isToday = dayjs(date).isSame(new Date(), "day");

            return (
              <Box
                style={{
                  backgroundColor: isToday ? "#faf0fd" : "transparent",
                  color: isToday ? "#862e9c" : "inherit",
                  border: isToday ? "1px solid #862e9c" : "none",
                  borderRadius: 6,
                  width: 34,
                  height: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: isToday ? 700 : 400,
                }}
              >
                {date.getDate()}
              </Box>
            );
          }}
        />
      </DatesProvider>
      <Checkbox
        label={t("form.reminder")}
        checked={formData.reminder_me}
        onChange={(event) => {
          const value = event.currentTarget.checked;
          setFormData((prev) => ({
            ...prev,
            reminder_me: value,
          }));
        }}
        mt="xs"
        ml="xs"
      />
      <Text fw={600} mb={12} mt={22}>
        {t("pick_color")}
      </Text>
      <Group mt="lg" justify="center">
        {Object.entries(colorMap).map(([key, value]) => (
          <ColorSwatch
            key={key}
            size={matches ? 22 : 25}
            color={value}
            component="button"
            onClick={() => {
              setFormData((prev) => ({ ...prev, display_color: key }));
            }}
            style={{
              cursor: "pointer",
              border:
                formData.display_color === key ? `2px solid ${value}` : "none",
              position: "relative",
            }}
          >
            {formData.display_color === key && <CheckIconTask />}
          </ColorSwatch>
        ))}
      </Group>

      <Group justify="space-between" mt="30px">
        <Button size="sm" variant="default" onClick={handleCancel}>
          {t("cancel")}
        </Button>
        <Button size="sm" onClick={handleSubmit}>
          {t("form.submit")}
        </Button>
      </Group>
    </Modal>
  );
}
